import { useEffect, useRef, useState, useCallback } from 'react';

interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

interface WebSocketOptions {
  url?: string;
  token?: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  heartbeatInterval?: number;
}

export const useWebSocket = (options: WebSocketOptions = {}) => {
  const {
    url = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`,
    token = localStorage.getItem('auth_token'),
    autoReconnect = true,
    reconnectInterval = 5000,
    heartbeatInterval = 30000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimer = useRef<NodeJS.Timeout | null>(null);
  const messageHandlers = useRef<Map<string, (data: any) => void>>(new Map());

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = token ? `${url}?token=${token}` : url;
    
    try {
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('[Native WebSocket] Connected to', wsUrl);
        setIsConnected(true);
        setConnectionError(null);

        // Start heartbeat
        if (heartbeatInterval > 0) {
          heartbeatTimer.current = setInterval(() => {
            if (ws.current?.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({ type: 'ping' }));
            }
          }, heartbeatInterval);
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          setLastMessage(message);
          
          // Handle message based on type
          const handler = messageHandlers.current.get(message.type);
          if (handler) {
            handler(message);
          }
          
          // Handle common message types
          switch (message.type) {
            case 'connected':
              console.log('WebSocket connection confirmed:', message.clientId);
              break;
            case 'error':
              console.error('WebSocket error:', message.message);
              setConnectionError(message.message);
              break;
            case 'pong':
              // Heartbeat response
              break;
            default:
              // Other message types handled by registered handlers
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[Native WebSocket] Connection error:', error);
        setConnectionError('WebSocket connection error');
      };

      ws.current.onclose = (event) => {
        console.log('[Native WebSocket] Disconnected - Code:', event.code, 'Reason:', event.reason);
        setIsConnected(false);

        // Clear heartbeat
        if (heartbeatTimer.current) {
          clearInterval(heartbeatTimer.current);
          heartbeatTimer.current = null;
        }

        // Auto-reconnect if enabled
        if (autoReconnect && !event.wasClean) {
          console.log(`[Native WebSocket] Attempting reconnection in ${reconnectInterval}ms...`);
          reconnectTimer.current = setTimeout(() => {
            console.log('[Native WebSocket] Reconnecting...');
            connect();
          }, reconnectInterval);
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setConnectionError('Failed to establish WebSocket connection');
    }
  }, [url, token, autoReconnect, reconnectInterval, heartbeatInterval]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    
    if (ws.current) {
      ws.current.close(1000, 'Client disconnecting');
      ws.current = null;
    }
    
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    }
    console.warn('WebSocket not connected, cannot send message');
    return false;
  }, []);

  const subscribeToRecording = useCallback((recordingId: string) => {
    return sendMessage({
      type: 'subscribe_recording',
      recordingId,
    });
  }, [sendMessage]);

  const unsubscribeFromRecording = useCallback((recordingId: string) => {
    return sendMessage({
      type: 'unsubscribe_recording',
      recordingId,
    });
  }, [sendMessage]);

  const subscribeToVenue = useCallback((venueId: string) => {
    return sendMessage({
      type: 'subscribe_venue',
      venueId,
    });
  }, [sendMessage]);

  const registerHandler = useCallback((messageType: string, handler: (data: any) => void) => {
    messageHandlers.current.set(messageType, handler);
    
    // Return cleanup function
    return () => {
      messageHandlers.current.delete(messageType);
    };
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    connectionError,
    sendMessage,
    subscribeToRecording,
    unsubscribeFromRecording,
    subscribeToVenue,
    registerHandler,
    connect,
    disconnect,
  };
};

// Specific hook for recording status updates
export const useRecordingStatus = (recordingId?: string) => {
  const [status, setStatus] = useState<string>('unknown');
  const [progress, setProgress] = useState<number>(0);
  const [metadata, setMetadata] = useState<any>({});
  
  const { 
    isConnected, 
    subscribeToRecording, 
    unsubscribeFromRecording,
    registerHandler 
  } = useWebSocket();

  useEffect(() => {
    if (!recordingId || !isConnected) return;

    // Subscribe to recording
    subscribeToRecording(recordingId);

    // Register status handler
    const cleanup = registerHandler('recording_status', (message) => {
      if (message.recordingId === recordingId) {
        setStatus(message.status);
        
        if (message.progress !== undefined) {
          setProgress(message.progress);
        }
        
        // Update metadata
        const { type, recordingId, status, progress, ...rest } = message;
        setMetadata(rest);
      }
    });

    // Cleanup on unmount or recording change
    return () => {
      unsubscribeFromRecording(recordingId);
      cleanup();
    };
  }, [recordingId, isConnected, subscribeToRecording, unsubscribeFromRecording, registerHandler]);

  return {
    status,
    progress,
    metadata,
    isConnected,
  };
};

// Hook for venue-wide recording updates
export const useVenueRecordings = (venueId?: string) => {
  const [activeRecordings, setActiveRecordings] = useState<Map<string, any>>(new Map());
  const [splData, setSplData] = useState<any>(null);

  const {
    isConnected,
    subscribeToVenue,
    registerHandler
  } = useWebSocket();

  // Fetch currently active recordings on initial load
  useEffect(() => {
    if (!venueId) {
      console.log('[useVenueRecordings] No venueId provided, skipping fetch');
      return;
    }

    console.log('[useVenueRecordings] Fetching active recordings for venue:', venueId);

    const fetchActiveRecordings = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const statuses = ['pending', 'queued', 'recording', 'uploading'];

        // Fetch all active recordings for this venue
        const promises = statuses.map(status =>
          fetch(`/api/recordings?venueId=${venueId}&status=${status}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json())
        );

        const results = await Promise.all(promises);
        const allRecordings = results.flat();

        console.log('[useVenueRecordings] Fetched recordings:', allRecordings.length, allRecordings);

        // Populate activeRecordings map
        if (allRecordings.length > 0) {
          console.log('[useVenueRecordings] Setting active recordings:', allRecordings.map(r => `${r.title} (${r.status})`));
          setActiveRecordings(new Map(
            allRecordings.map(rec => [
              rec.id,
              {
                id: rec.id,
                title: rec.title,
                status: rec.status,
                startTime: rec.start_time,
                start_time: rec.start_time,
                captureMinutes: rec.capture_minutes,
                capture_minutes: rec.capture_minutes,
                venueId: rec.venue_id,
                venueName: rec.venue_name,
                venue_name: rec.venue_name,
                deviceId: rec.device_id,
                deviceName: rec.device_name,
                device_name: rec.device_name,
                artistId: rec.artist_id,
                artistName: rec.artist_name,
                artist_name: rec.artist_name,
              }
            ])
          ));
        } else {
          console.log('[useVenueRecordings] No active recordings found');
        }
      } catch (error) {
        console.error('[useVenueRecordings] Failed to fetch active recordings:', error);
      }
    };

    fetchActiveRecordings();
  }, [venueId]);

  useEffect(() => {
    if (!venueId || !isConnected) return;

    // Subscribe to venue
    subscribeToVenue(venueId);

    // Register handlers
    const handlers: (() => void)[] = [];

    handlers.push(
      registerHandler('recording_started', (message) => {
        if (message.venueId === venueId) {
          setActiveRecordings(prev => {
            const next = new Map(prev);
            next.set(message.recordingId, {
              id: message.recordingId,
              status: 'recording',
              startTime: message.startTime,
              ...message,
            });
            return next;
          });
        }
      })
    );

    handlers.push(
      registerHandler('recording_stopped', (message) => {
        if (message.venueId === venueId) {
          setActiveRecordings(prev => {
            const next = new Map(prev);
            const recording = next.get(message.recordingId);
            if (recording) {
              next.set(message.recordingId, {
                ...recording,
                status: 'stopped',
                endTime: message.endTime,
                ...message,
              });
            }
            return next;
          });
        }
      })
    );

    handlers.push(
      registerHandler('recording_ready', (message) => {
        if (message.venueId === venueId) {
          setActiveRecordings(prev => {
            const next = new Map(prev);
            next.delete(message.recordingId);
            return next;
          });
        }
      })
    );

    handlers.push(
      registerHandler('spl_update', (message) => {
        if (message.venueId === venueId) {
          setSplData(message.spl);
        }
      })
    );

    // Cleanup
    return () => {
      handlers.forEach(cleanup => cleanup());
    };
  }, [venueId, isConnected, subscribeToVenue, registerHandler]);

  return {
    activeRecordings: Array.from(activeRecordings.values()),
    splData,
    isConnected,
  };
};

export default useWebSocket;